import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Grid,
  GridItem,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { FaStar, FaDollarSign, FaCoins, FaGem, FaTrophy, FaCrown } from 'react-icons/fa';

/**
 * ScratchCardPreview Component
 * 
 * Provides a visual preview of a scratch card based on its configuration.
 * Can be used in both interactive and non-interactive modes.
 */
const ScratchCardPreview = ({ config, revealed = false, interactive = false, onReveal }) => {
  const [revealedCells, setRevealedCells] = useState([]);
  const [isRevealed, setIsRevealed] = useState(revealed);
  
  // Default configuration if none is provided
  const defaultConfig = {
    layout: {
      gridSize: { rows: 3, columns: 3 },
      style: { borderRadius: '8px', backgroundColor: '#1a365d' }
    },
    symbols: [
      { type: 'dollar', value: 100 },
      { type: 'dollar', value: 200 },
      { type: 'dollar', value: 500 }
    ],
    theme: {
      primaryColor: '#3182CE',
      secondaryColor: '#2C5282',
      textColor: '#FFFFFF'
    }
  };
  
  // Use provided config or default
  const cardConfig = config || defaultConfig;
  
  // Reset revealed cells when config changes
  useEffect(() => {
    setRevealedCells([]);
    setIsRevealed(revealed);
  }, [cardConfig, revealed]);
  
  // Handle cell click for interactive mode
  const handleCellClick = (rowIndex, colIndex) => {
    if (!interactive) return;
    
    const cellId = `${rowIndex}-${colIndex}`;
    if (!revealedCells.includes(cellId)) {
      const newRevealedCells = [...revealedCells, cellId];
      setRevealedCells(newRevealedCells);
      
      // Check if all cells are revealed
      if (cardConfig.layout && cardConfig.layout.gridSize) {
        const totalCells = cardConfig.layout.gridSize.rows * cardConfig.layout.gridSize.columns;
        if (newRevealedCells.length === totalCells) {
          setIsRevealed(true);
          if (onReveal) onReveal();
        }
      }
    }
  };
  
  // Get symbol component based on symbol type
  const getSymbolComponent = (symbolType) => {
    const symbols = {
      'star': FaStar,
      'dollar': FaDollarSign,
      'coin': FaCoins,
      'gem': FaGem,
      'trophy': FaTrophy,
      'crown': FaCrown
    };
    
    return symbols[symbolType] || FaStar;
  };
  
  // Generate symbols for the grid
  const generateSymbols = () => {
    if (!cardConfig.layout || !cardConfig.layout.gridSize || !cardConfig.symbols) {
      console.error('Invalid card configuration:', cardConfig);
      return [];
    }
    
    const rows = cardConfig.layout.gridSize.rows;
    const cols = cardConfig.layout.gridSize.columns;
    const symbols = [];
    
    // Create a grid of symbols
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        // Use the symbol from the config, or fallback to a random one
        const symbolIndex = (i * cols + j) % cardConfig.symbols.length;
        symbols.push({
          ...cardConfig.symbols[symbolIndex],
          row: i,
          col: j
        });
      }
    }
    
    return symbols;
  };
  
  // Generate prize values for the grid
  const generatePrizes = () => {
    const rows = cardConfig.layout?.gridSize?.rows || 3;
    const cols = cardConfig.layout?.gridSize?.columns || 3;
    const prizes = [];
    
    // Create a grid of prizes
    for (let i = 0; i < rows; i++) {
      const rowPrizes = [];
      for (let j = 0; j < cols; j++) {
        // Generate a random prize value between 10 and 1000
        const prizeValue = Math.floor(Math.random() * 990) + 10;
        rowPrizes.push(prizeValue);
      }
      prizes.push(rowPrizes);
    }
    
    return prizes;
  };
  
  // Generate symbols and prizes
  const symbols = generateSymbols();
  const prizes = generatePrizes();
  
  // UI colors
  const cardBgColor = (cardConfig.theme?.primaryColor) || '#1a365d';
  const cardAccentColor = (cardConfig.theme?.secondaryColor) || 'blue.400';
  const cellBgColor = useColorModeValue('gray.100', 'gray.700');
  const cellHoverColor = useColorModeValue('gray.200', 'gray.600');
  const symbolColor = useColorModeValue('yellow.500', 'yellow.300');
  
  // Render the grid
  const renderGrid = () => {
    // If we couldn't generate symbols, show a placeholder
    if (symbols.length === 0) {
      return (
        <Box 
          width="100%" 
          height="100%" 
          bg="gray.700" 
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="white">Card Preview</Text>
        </Box>
      );
    }
    
    const rows = cardConfig.layout.gridSize.rows;
    const cols = cardConfig.layout.gridSize.columns;
    
    return (
      <Grid
        templateColumns={`repeat(${cols}, 1fr)`}
        templateRows={`repeat(${rows}, 1fr)`}
        gap={2}
        width="100%"
        height="100%"
      >
        {symbols.map((symbol, index) => {
          const cellId = `${symbol.row}-${symbol.col}`;
          const isCellRevealed = isRevealed || revealedCells.includes(cellId);
          const SymbolIcon = getSymbolComponent(symbol.type);
          
          return (
            <GridItem
              key={cellId}
              bg={isCellRevealed ? cardAccentColor : cellBgColor}
              borderRadius="md"
              cursor={interactive ? 'pointer' : 'default'}
              onClick={() => handleCellClick(symbol.row, symbol.col)}
              transition="all 0.2s"
              _hover={interactive ? { bg: cellHoverColor } : {}}
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              overflow="hidden"
            >
              {isCellRevealed ? (
                <VStack spacing={0}>
                  <Icon as={SymbolIcon} color={symbolColor} boxSize={6} />
                  <Text fontWeight="bold" fontSize="sm">
                    {prizes[symbol.row] && prizes[symbol.row][symbol.col]}
                  </Text>
                </VStack>
              ) : (
                <Box
                  width="100%"
                  height="100%"
                  bg={cellBgColor}
                  opacity={0.8}
                  position="absolute"
                />
              )}
            </GridItem>
          );
        })}
      </Grid>
    );
  };
  
  return (
    <Box
      width="100%"
      height="100%"
      borderRadius={(cardConfig.layout?.style?.borderRadius) || '8px'}
      overflow="hidden"
      bg={cardBgColor}
      boxShadow="0 4px 8px rgba(0, 0, 0, 0.2)"
      position="relative"
      p={4}
    >
      <VStack spacing={1} mb={4}>
        <Text 
          fontSize="xl" 
          fontWeight="bold" 
          color="white"
          textAlign="center"
        >
          {cardConfig.title || "Scratch Card"}
        </Text>
        
        <HStack spacing={2}>
          <Badge colorScheme="green">
            Win up to 1000
          </Badge>
          <Badge colorScheme="purple">
            {cardConfig.difficulty || "Medium"}
          </Badge>
        </HStack>
      </VStack>
      
      {renderGrid()}
    </Box>
  );
};

export default ScratchCardPreview;
